<script setup lang="ts">
/**
 * MovementTabs
 *
 * Tab-Leiste für mehrteilige PDF-Uploads (Movements). Jedes Movement ist eine eigene
 * Arbeitsdatei (Lineage), gruppiert über die ``documentId`` des aktiven Dokuments. Ein Klick
 * aktiviert das Movement als Arbeitsbasis (``useFileHistoryStore.switchMovement``); der Rest der
 * App (Vorschau/Transpose/Convert) arbeitet unverändert mit dem aktiven Movement.
 *
 * Rendert nichts, wenn weniger als zwei Movements vorhanden sind (Einzeldatei-Upload).
 */
import {computed} from 'vue';
import {storeToRefs} from 'pinia';
import {Icon} from '@iconify/vue';
import {useFileHistoryStore} from '@/stores/useFileHistoryStore';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore';
import type {HistoryEntry} from '@/services/historyDb';

const historyStore = useFileHistoryStore();
const uploadStore = useMusicUploadStore();
const {activeDocumentMovements, activeMovementIndex} = storeToRefs(historyStore);
const {isPending} = storeToRefs(uploadStore);

const hasMovements = computed(() => activeDocumentMovements.value.length > 1);

/** Anzeigename eines Movements (vergebener Name > Dateiname > „Teil N"). */
function labelFor(entry: HistoryEntry, index: number): string {
  return entry.name || entry.filename || `Teil ${index + 1}`;
}

function selectMovement(index: number): void {
  if (isPending.value || index === activeMovementIndex.value) return;
  void historyStore.switchMovement(index);
}
</script>

<template>
  <nav
      v-if="hasMovements"
      class="flex  flex-wrap gap-2 rounded-sm  border-2 border-orange-500/80  bg-blue-100/50 shadow-sm p-1.5"
      aria-label="Teile (Movements)"
  >
    <button
        v-for="(movement, index) in activeDocumentMovements"
        :key="movement.id"
        type="button"
        :disabled="isPending"
        class="flex animate-flip-up hover:animate-once  items-center  rounded-sm p-3 text-lg font-medium leading-5 transition-all gap-2 duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
        :class="[
          index === activeMovementIndex
            ? 'bg-white text-blue-700 shadow-sm'
            : 'text-rose-500/80 bg-white/30  hover:bg-white/70 hover:text-gray-700',
          isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        ]"
        :aria-current="index === activeMovementIndex ? 'true' : undefined"
        @click="selectMovement(index)"
    >
      <Icon
          icon="fa6-solid:music"
          class="text-xs"
          :class="index === activeMovementIndex ? 'text-blue-600' : 'text-rose-500'"
      />
      <span class="">{{ labelFor(movement, index) }}</span>
    </button>
  </nav>
</template>
